import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DocumentsAPI } from '@/services/documentsAPI';
import { Document, SignatureField } from '@/types/documents';
import Page from '@/components/Page';
import Card from '@/components/Card';
import Stack from '@/components/Stack';
import { Body, Small } from '@/components/Typography';
import Button from '@/components/Button';
import Alert from '@/components/Alert';
import LoadingState from '@/components/LoadingState';
import ROUTES from '@/routes/routes';
import { getFieldConfig } from '@/utils/pdfFieldConfig';
import { loadPdfJs } from '@/utils/pdfLoader';

const TestSignView: React.FC = () => {
  const navigate = useNavigate();
  const { documentId } = useParams<{ documentId: string }>();

  const [loading, setLoading] = useState(true);
  const [doc, setDoc] = useState<Document | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [signing, setSigning] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);

  // PDF state
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1);
  const [pageHeight, setPageHeight] = useState(792);
  const [pdfError, setPdfError] = useState<string | null>(null);

  // Signature pad state
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const pdfCanvasRef = useRef<HTMLCanvasElement>(null);
  const sigCanvasRef = useRef<HTMLCanvasElement>(null);
  const pdfDocRef = useRef<any>(null);

  // Fetch document
  useEffect(() => {
    if (!documentId) return;
    fetchDocument();
  }, [documentId]);

  const fetchDocument = async () => {
    setLoading(true);
    try {
      const response = await DocumentsAPI.getDocuments();
      if (response.success) {
        const found = response.data.find((d) => d.id === Number(documentId));
        if (found) {
          setDoc(found);
          // Navigate to the first page that has fields
          const fields = found.latest_version?.signature_fields;
          if (fields?.length) {
            setCurrentPage(fields[0].page);
          } else if (found.latest_version?.signature_page) {
            setCurrentPage(found.latest_version.signature_page);
          }
        } else {
          setError('Document not found');
        }
      }
    } catch {
      setError('Failed to load document');
    } finally {
      setLoading(false);
    }
  };

  // Load and render PDF
  useEffect(() => {
    const version = doc?.latest_version;
    if (!version?.download_url) return;

    let cancelled = false;

    async function loadAndRender() {
      try {
        const lib = await loadPdfJs();
        const pdfDoc = await lib.getDocument(version!.download_url).promise;
        if (cancelled) return;
        pdfDocRef.current = pdfDoc;
        setNumPages(pdfDoc.numPages);

        const sigPage = version!.signature_page || 1;
        if (sigPage > pdfDoc.numPages) setCurrentPage(1);
      } catch (err: any) {
        if (!cancelled) setPdfError(`Failed to load PDF: ${err.message}`);
      }
    }

    loadAndRender();
    return () => {
      cancelled = true;
    };
  }, [doc]);

  // Render current page
  useEffect(() => {
    const pdfDoc = pdfDocRef.current;
    if (!pdfDoc || currentPage < 1 || currentPage > numPages) return;

    let cancelled = false;

    async function renderPage() {
      try {
        const page = await pdfDoc.getPage(currentPage);
        if (cancelled) return;

        const viewport = page.getViewport({ scale: 1 });
        setPageHeight(viewport.height);

        let newScale = 1;
        if (containerRef.current) {
          const containerWidth = containerRef.current.clientWidth - 32;
          newScale = Math.min(containerWidth / viewport.width, 1.5);
        }
        setScale(newScale);

        const scaledViewport = page.getViewport({ scale: newScale });
        const canvas = pdfCanvasRef.current;
        if (!canvas || cancelled) return;

        canvas.width = scaledViewport.width;
        canvas.height = scaledViewport.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        await page.render({ canvasContext: ctx, viewport: scaledViewport })
          .promise;
      } catch (err: any) {
        if (!cancelled) setPdfError(`Failed to render page: ${err.message}`);
      }
    }

    renderPage();
    return () => {
      cancelled = true;
    };
  }, [numPages, currentPage]);

  // Signature pad drawing handlers
  const getCanvasPos = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = sigCanvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  };

  const handleSigMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = sigCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    setIsDrawing(true);
    const pos = getCanvasPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  };

  const handleSigMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    const canvas = sigCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const pos = getCanvasPos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = '#1a1a2e';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
    setHasSignature(true);
  };

  const handleSigMouseUp = () => {
    setIsDrawing(false);
  };

  const handleClearSignature = () => {
    const canvas = sigCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const handleSign = async () => {
    if (!doc || !hasSignature) return;

    const canvas = sigCanvasRef.current;
    if (!canvas) return;

    setSigning(true);
    setError(null);

    try {
      const signatureData = canvas.toDataURL('image/png');
      const response = await DocumentsAPI.testSign(doc.id, signatureData);

      if (response.success) {
        setSuccess('Document signed successfully!');
        setDownloadUrl(response.data.download_url);
      }
    } catch (err: any) {
      setError(
        err.response?.data?.errors?.join(', ') || 'Failed to sign document',
      );
    } finally {
      setSigning(false);
    }
  };

  const version = doc?.latest_version;

  // Get all signature fields (new format or construct from old coords)
  const allFields: SignatureField[] = (() => {
    if (version?.signature_fields?.length) return version.signature_fields;
    if (version?.has_signature_coordinates) {
      return [
        {
          field_type: 'signature' as const,
          page: version.signature_page || 1,
          x: version.signature_x || 0,
          y: version.signature_y || 0,
          width: 200,
          height: 60,
          sort_order: 0,
        },
      ];
    }
    return [];
  })();

  // Fields on the current page
  const currentPageFields = allFields.filter((f) => f.page === currentPage);

  // Pages that have fields (for "go to" button)
  const fieldPages = [...new Set(allFields.map((f) => f.page))].sort(
    (a, b) => a - b,
  );
  const firstFieldPage = fieldPages[0] || 1;

  if (loading) {
    return (
      <Page title="Test Sign Document">
        <Card>
          <LoadingState />
        </Card>
      </Page>
    );
  }

  if (!doc || !version) {
    return (
      <Page title="Test Sign Document">
        <Card>
          <Card.Section>
            <Body>Document not found or has no versions.</Body>
          </Card.Section>
        </Card>
      </Page>
    );
  }

  return (
    <Page
      breadcrumbs={[{ label: 'Documents', to: `/${ROUTES.documents}` }]}
      size="md"
      title={`Test Sign — ${doc.name}`}
    >
      {error && (
        <div style={{ marginBottom: '16px' }}>
          <Alert
            description={error}
            status="danger"
            title="Error"
            onDismiss={() => setError(null)}
          />
        </div>
      )}

      {success && (
        <div style={{ marginBottom: '16px' }}>
          <Alert description={success} status="success" title="Success" />
        </div>
      )}

      {!version.has_signature_coordinates && (
        <div style={{ marginBottom: '16px' }}>
          <Alert
            description="This document does not have a signature position configured. Edit the document to set one first."
            status="warning"
            title="Signature position not set"
          />
        </div>
      )}

      <Card>
        <Card.Header title="Document Preview" />
        <Card.Section>
          <div ref={containerRef}>
            <Stack gap={8} style={{ marginBottom: '12px' }}>
              <Button
                a11yLabel="Previous page"
                appearance="outline"
                label="Prev"
                disabled={currentPage <= 1}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              />
              <Body>
                Page {currentPage} of {numPages || '...'}
              </Body>
              <Button
                a11yLabel="Next page"
                appearance="outline"
                label="Next"
                disabled={currentPage >= numPages}
                onClick={() => setCurrentPage((p) => Math.min(numPages, p + 1))}
              />
              {fieldPages.length > 0 && !fieldPages.includes(currentPage) && (
                <Button
                  a11yLabel="Go to first page with fields"
                  appearance="outline"
                  label={`Go to Field Page (${firstFieldPage})`}
                  onClick={() => setCurrentPage(firstFieldPage)}
                />
              )}
            </Stack>

            <div
              style={{
                border: '1px solid #ddd',
                borderRadius: '4px',
                overflow: 'auto',
                maxHeight: '600px',
                background: '#f5f5f5',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              {pdfError ? (
                <div style={{ padding: '20px', color: 'red' }}>
                  <Body>{pdfError}</Body>
                </div>
              ) : (
                <div style={{ position: 'relative' }}>
                  <canvas ref={pdfCanvasRef} style={{ display: 'block' }} />
                  {/* Show field indicators on current page */}
                  {currentPageFields.map((field, idx) => {
                    const config = getFieldConfig(field.field_type);
                    const screenFieldX = field.x * scale;
                    const screenFieldY =
                      (pageHeight - field.y - field.height) * scale;
                    const screenFieldW = field.width * scale;
                    const screenFieldH = field.height * scale;

                    return (
                      <div
                        key={idx}
                        style={{
                          position: 'absolute',
                          left: `${screenFieldX}px`,
                          top: `${screenFieldY}px`,
                          width: `${screenFieldW}px`,
                          height: `${screenFieldH}px`,
                          border: `2px dashed ${config.color}`,
                          borderRadius: '4px',
                          backgroundColor: config.bgColorLight,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          pointerEvents: 'none',
                          zIndex: 10,
                        }}
                      >
                        <span
                          style={{
                            fontSize: '10px',
                            color: config.color,
                            fontWeight: 600,
                          }}
                        >
                          {config.testLabel}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </Card.Section>
      </Card>

      {version.has_signature_coordinates && !downloadUrl && (
        <div style={{ marginTop: '24px' }}>
          <Card>
            <Card.Header title="Draw Your Signature" />
            <Card.Section>
              <Stack gap={16} vertical>
                <Small>
                  Draw your signature in the box below using your mouse, then
                  click "Sign Document".
                </Small>
                <div
                  style={{
                    border: '2px solid #ddd',
                    borderRadius: '4px',
                    backgroundColor: '#fff',
                    cursor: 'crosshair',
                  }}
                >
                  <canvas
                    ref={sigCanvasRef}
                    width={400}
                    height={120}
                    style={{ display: 'block', width: '100%', height: '120px' }}
                    onMouseDown={handleSigMouseDown}
                    onMouseMove={handleSigMouseMove}
                    onMouseUp={handleSigMouseUp}
                    onMouseLeave={handleSigMouseUp}
                  />
                </div>
                <Stack gap={8}>
                  <Button
                    a11yLabel="Clear signature"
                    appearance="outline"
                    label="Clear"
                    onClick={handleClearSignature}
                  />
                </Stack>
              </Stack>
            </Card.Section>
            <Card.Section subdued>
              <Stack justify="end">
                <Button
                  a11yLabel="Sign the document"
                  isLoading={signing}
                  disabled={!hasSignature}
                  label="Sign Document"
                  onClick={handleSign}
                />
              </Stack>
            </Card.Section>
          </Card>
        </div>
      )}

      {downloadUrl && (
        <div style={{ marginTop: '24px' }}>
          <Card>
            <Card.Header title="Signed Document" />
            <Card.Section>
              <Stack gap={16} vertical>
                <Body>
                  The document has been signed successfully. You can download
                  the signed PDF below.
                </Body>
                <Stack gap={8}>
                  <Button
                    a11yLabel="Download signed PDF"
                    label="Download Signed PDF"
                    onClick={() => window.open(downloadUrl, '_blank')}
                  />
                  <Button
                    a11yLabel="Back to documents"
                    appearance="outline"
                    label="Back to Documents"
                    onClick={() => navigate(`/${ROUTES.documents}`)}
                  />
                </Stack>
              </Stack>
            </Card.Section>
          </Card>
        </div>
      )}
    </Page>
  );
};

export default TestSignView;
